import { useEffect } from 'react';

import { Center, Stack } from '@mantine/core';
import { useScrollIntoView } from '@mantine/hooks';

import { Navigate, useParams } from 'react-router-dom';

import AssistantPageLayout from '../../features/bidResponse/components/assistant/components/AssistantPageLayout';
import { LoaderMessage } from '../../features/bidResponse/components/assistant/components/LoaderMessage';
import { MessageInput } from '../../features/bidResponse/components/assistant/components/MessageInput';
import ThreadMessage from '../../features/bidResponse/components/assistant/components/ThreadMessage';
import {
  useCreateThreadMessage,
  useGetThreadById,
} from '../../features/bidResponse/components/assistant/hooks/assistant.hook';
import { useGenerateAnswer } from '../../features/bidResponse/components/assistant/hooks/useGenerateAnswer.hook';
import {
  type AssistantThreadFilters,
  AssistantThreadMessageRole,
} from '../../shared/entities/AssistantThreadMessage';

export default function AssistantThreadPage() {
  const { id } = useParams();

  const threadId = id ? parseInt(id) : undefined;

  if (!threadId) {
    return <Navigate to="/not-found" replace />;
  }

  return <SafeAssistantThreadPage threadId={threadId} />;
}

function SafeAssistantThreadPage({ threadId }: { threadId: number }) {
  const {
    data: { title = '' },
  } = useGetThreadById(threadId);

  return (
    <AssistantPageLayout>
      <AssistantPageLayout.Title label={title} />
      <AssistantPageLayout.Content>
        <Chat threadId={threadId} />
      </AssistantPageLayout.Content>
    </AssistantPageLayout>
  );
}

function Chat({ threadId }: { threadId: number }) {
  const {
    data: { messages = [] },
  } = useGetThreadById(threadId);
  const { generateAnswer, isPending } = useGenerateAnswer();

  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<
    HTMLDivElement,
    HTMLDivElement
  >({ duration: 0 });

  useEffect(() => {
    scrollIntoView({ alignment: 'end' });
  }, [scrollIntoView, messages]);

  const lastMessage = messages.at(-1);
  const isLastMessageUser =
    lastMessage?.role === AssistantThreadMessageRole.USER;

  useEffect(() => {
    if (isLastMessageUser) {
      generateAnswer(threadId);
    }
  }, [isLastMessageUser, generateAnswer, threadId]);

  return (
    <>
      <Stack
        spacing="04"
        ref={scrollableRef}
        pt="06"
        px="10"
        sx={{ flex: 1, overflowY: 'auto' }}
      >
        {messages.map(message => (
          <ThreadMessage key={message.id} message={message} />
        ))}

        {isLastMessageUser && <LoaderMessage />}

        <div ref={targetRef} />
      </Stack>

      <ChatInput
        threadId={threadId}
        disabled={isLastMessageUser || isPending}
      />
    </>
  );
}

type ChatInputProps = {
  threadId: number;
  disabled: boolean;
};

function ChatInput({ threadId, disabled }: ChatInputProps) {
  const createThreadMessageMutation = useCreateThreadMessage(threadId);

  const handleSubmit = (content: string, filters: AssistantThreadFilters) => {
    createThreadMessageMutation.mutate({ content, filters });
  };

  const isInputDisabled = disabled || createThreadMessageMutation.isPending;

  return (
    <Center>
      <MessageInput
        size="sm"
        onSubmit={handleSubmit}
        threadId={threadId}
        isDisabled={isInputDisabled}
      />
    </Center>
  );
}
